import { native } from "./native";

/** 서버 오류. code 는 서버 ERROR_CODES (예: CUSTOMER_INACTIVE). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
  }
}

type TokenSource = {
  get: () => string | null;
  /** 401 이면 한 번 새로 받아 본다. 실패하면 null. */
  renew: () => Promise<string | null>;
};
let tokens: TokenSource = { get: () => null, renew: async () => null };
export function setTokenSource(t: TokenSource): void {
  tokens = t;
}

const clientHeader = () => (native() ? `electron/${native()!.version}` : "web");

async function raw(method: string, path: string, body: unknown, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { "X-Meta-Crew-Client": clientHeader() };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`/v1${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "same-origin" });
}

export type Page<T> = { items: T[]; total: number };

async function parse(res: Response): Promise<{ result: unknown; totalCount?: number }> {
  const text = await res.text();
  let json: Record<string, unknown> = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    /* HTML 오류 페이지 등 */
  }
  if (!res.ok) {
    const msg = (json.message as string | string[] | undefined) ?? res.statusText;
    throw new ApiError(res.status, Array.isArray(msg) ? msg.join(", ") : String(msg || "요청에 실패했습니다."), json.code as string | undefined, json.detail as Record<string, unknown> | undefined);
  }
  const data = (json.data ?? {}) as { result: unknown; totalCount?: number };
  return data;
}

/** renew=false: 401 이 "로그인 만료"가 아니라 "비밀번호 틀림"인 요청(개인정보 보기 재확인)은 토큰 갱신·재시도를 하지 않는다 (시도 횟수 이중 소모 방지). */
export async function request<T>(method: string, path: string, body?: unknown, opts: { auth?: boolean; renew?: boolean } = {}): Promise<{ result: T; total?: number }> {
  const useAuth = opts.auth !== false;
  let res = await raw(method, path, body, useAuth ? tokens.get() : null);
  if (res.status === 401 && useAuth && opts.renew !== false) {
    const next = await tokens.renew();
    if (next) res = await raw(method, path, body, next);
  }
  const d = await parse(res);
  return { result: d.result as T, total: d.totalCount };
}

export const api = {
  get: async <T>(path: string) => (await request<T>("GET", path)).result,
  page: async <T>(path: string): Promise<Page<T>> => {
    const r = await request<T[]>("GET", path);
    return { items: r.result ?? [], total: r.total ?? r.result?.length ?? 0 };
  },
  post: async <T>(path: string, body?: unknown) => (await request<T>("POST", path, body ?? {})).result,
  put: async <T>(path: string, body?: unknown) => (await request<T>("PUT", path, body ?? {})).result,
  patch: async <T>(path: string, body?: unknown) => (await request<T>("PATCH", path, body ?? {})).result,
  del: async <T>(path: string) => (await request<T>("DELETE", path)).result,
  /** 인증이 필요한 파일(캡처 등)을 받아 object URL 로. 쓰고 나면 URL.revokeObjectURL. */
  blobUrl: async (path: string): Promise<string> => {
    let res = await raw("GET", path, undefined, tokens.get());
    if (res.status === 401) {
      const t = await tokens.renew();
      if (t) res = await raw("GET", path, undefined, t);
    }
    if (!res.ok) throw new ApiError(res.status, "파일을 불러오지 못했습니다.");
    return URL.createObjectURL(await res.blob());
  },
  /** 첨부 업로드 (multipart). */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const form = new FormData();
    form.append("file", file);
    const send = (t: string | null) =>
      fetch(`/v1${path}`, { method: "POST", body: form, headers: { "X-Meta-Crew-Client": clientHeader(), ...(t ? { Authorization: `Bearer ${t}` } : {}) } });
    let res = await send(tokens.get());
    if (res.status === 401) {
      const t = await tokens.renew();
      if (t) res = await send(t);
    }
    return (await parse(res)).result as T;
  },
  /**
   * 큰 파일 업로드 (multipart, 진행률). fetch 는 업로드 진행률을 주지 않아 XHR 을 쓴다.
   * fields 는 파일보다 먼저 붙인다. onProgress(보낸 바이트, 전체 바이트).
   */
  uploadWithProgress: async <T>(path: string, file: File, opts: { fields?: Record<string, string>; onProgress?: (sent: number, total: number) => void } = {}): Promise<T> => {
    const form = new FormData();
    for (const [k, v] of Object.entries(opts.fields ?? {})) form.append(k, v);
    form.append("file", file);
    const send = (t: string | null) =>
      new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `/v1${path}`);
        xhr.setRequestHeader("X-Meta-Crew-Client", clientHeader());
        if (t) xhr.setRequestHeader("Authorization", `Bearer ${t}`);
        xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : file.size);
        xhr.onload = () => resolve(new Response(xhr.responseText, { status: xhr.status }));
        xhr.onerror = () => reject(new TypeError("upload failed"));
        xhr.send(form);
      });
    let res = await send(tokens.get());
    if (res.status === 401) {
      const t = await tokens.renew();
      if (t) res = await send(t);
    }
    return (await parse(res)).result as T;
  },
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403 && e.code === "CUSTOMER_INACTIVE") return "고객사 서비스 상태 때문에 지금은 할 수 없습니다.";
    if (e.status === 403) return e.message || "권한이 없습니다.";
    return e.message;
  }
  if (e instanceof TypeError) return "서버에 연결할 수 없습니다. 네트워크를 확인하세요.";
  return e instanceof Error ? e.message : String(e);
}
